import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowUpRight, LockKeyhole } from "lucide-react";
import { register, getCaptchaConfig } from "../lib/api";
import CaptchaTurnstile from "../components/CaptchaTurnstile";
import { homeFor } from "../lib/admin";
import { REGISTER } from "../constants/testIds/auth";
import { PRODUCT_IDENTITY } from "../lib/story";
import PublicNav from "../components/PublicNav";
import { planLabel, normalizeSignupPlan, SIGNUP_PLAN_OPTIONS } from "../lib/plans";
import {
  captureUtmFromUrl,
  getVisitorSessionId,
  trackVisitorEvent,
} from "../lib/visitorAnalytics";

export default function Register({ onLogin }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialPlan = normalizeSignupPlan(params.get("plan"));
  const [selectedPlan, setSelectedPlan] = useState(initialPlan);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaConfig, setCaptchaConfig] = useState({ enabled: false, site_key: null });

  useEffect(() => {
    getCaptchaConfig()
      .then(({ data }) => setCaptchaConfig(data))
      .catch(() => setCaptchaConfig({ enabled: false, site_key: null }));
  }, []);

  const captchaRequired = Boolean(captchaConfig.enabled && captchaConfig.site_key);

  useEffect(() => {
    captureUtmFromUrl();
    const plan = normalizeSignupPlan(params.get("plan"));
    setSelectedPlan(plan);
    trackVisitorEvent("signup_started", { plan });
    if (plan === "professional") trackVisitorEvent("professional_requested", { source: "signup" });
    if (plan === "trial") trackVisitorEvent("trial_started", { source: "signup" });
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await register({
        name,
        email: email.trim(),
        password,
        password_confirm: confirm,
        selected_plan: selectedPlan,
        visitor_session_id: getVisitorSessionId(),
        captcha_token: captchaRequired ? captchaToken : undefined,
      });
      trackVisitorEvent("signup_completed", { plan: selectedPlan });
      const ok = await onLogin();
      if (!ok) {
        setError("Account created, but the session could not be verified.");
        return;
      }
      navigate(homeFor(data), { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not create the account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login-screen login-screen-auth" data-testid="register-page">
      <div className="login-grid" />
      <PublicNav />
      <section className="login-hero">
        <div className="eyebrow"><span className="signal" /> NEXTPROMIND · YUG360-MIND</div>
        <h1>Create a workspace<br /><em>account.</em></h1>
        <p>New users join Northstar (NRG-01) and see the existing portfolio. Seeded operators are not overwritten. Role is App Owner — it cannot authorize MDRs.</p>
        <Link data-testid="register-back-login" className="text-button" to="/login">Back to sign in</Link>
      </section>
      <form className="login-panel" onSubmit={submit}>
        <div className="brand-mark">YUG<span>360</span><small>— MIND</small></div>
        <p className="kicker">{PRODUCT_IDENTITY.toUpperCase()}</p>
        <h2>Create an account</h2>
        <p className="muted" data-testid="register-selected-plan">
          Selected plan: <strong>{planLabel(selectedPlan)}</strong>
        </p>
        <label htmlFor="register-plan">Plan
          <select
            id="register-plan"
            data-testid="register-plan-select"
            value={selectedPlan}
            onChange={(e) => setSelectedPlan(normalizeSignupPlan(e.target.value))}
          >
            {SIGNUP_PLAN_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </label>
        <label htmlFor="register-name">Name
          <input id="register-name" data-testid={REGISTER.nameInput} name="name" autoComplete="name" required value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label htmlFor="register-email">Email
          <input id="register-email" data-testid={REGISTER.emailInput} type="email" name="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label htmlFor="register-password">Password
          <input id="register-password" data-testid={REGISTER.passwordInput} type="password" name="password" autoComplete="new-password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        <label htmlFor="register-password-confirm">Confirm password
          <input id="register-password-confirm" data-testid={REGISTER.passwordConfirmInput} type="password" name="password_confirm" autoComplete="new-password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </label>
        {captchaRequired && (
          <CaptchaTurnstile
            siteKey={captchaConfig.site_key}
            onToken={setCaptchaToken}
            onExpire={() => setCaptchaToken("")}
          />
        )}
        <div className="login-actions">
          <button data-testid={REGISTER.submitButton} className="primary-button full" type="submit" disabled={loading || (captchaRequired && !captchaToken)}>
            {loading ? "Creating…" : "Create account"} <ArrowUpRight size={16} />
          </button>
        </div>
        <div className="login-links">
          <Link data-testid={REGISTER.loginLink} to="/login">Already have an account</Link>
          <Link to="/login/sso">Continue with SSO</Link>
          <Link to="/contact-sales?plan=enterprise">Contact Sales</Link>
        </div>
        {error && <div data-testid="register-error" className="error-message">{error}</div>}
        <div className="login-footer"><LockKeyhole size={14} /> Cannot self-assign Platform Admin, Tenant Admin or AI</div>
      </form>
    </main>
  );
}
